import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';
import ShipmentDetailModal from '../components/deliverer/ShipmentDetailModal';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'deliverer_token';

// Mismo listado que /cotizador. El entregador puede elegir desde cuál salir.
const BRANCHES = [
  { id: 'morelos', name: 'Sucursal Morelos', lng: -100.94424590860048, lat: 25.42114314472833 },
  { id: 'america', name: 'Sucursal América', lng: -100.96496449312514, lat: 25.43860048729518 },
];

const DEFENSIVE_CSS = `
  .mapboxgl-map { position: relative; width: 100%; height: 100%; }
  .mapboxgl-canvas-container, .mapboxgl-canvas { width: 100% !important; height: 100% !important; }
`;

// Axios con token del entregador
const dax = axios.create({ baseURL: API });
dax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

export default function DelivererPendingMap({ onBack }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);   // markers de destinos
  const branchMarkerRef = useRef(null);

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapError, setMapError] = useState('');

  const [branch, setBranch] = useState(BRANCHES[0]);
  const [roundtrip, setRoundtrip] = useState(true); // true = ida y vuelta, false = solo ida
  const [optimizing, setOptimizing] = useState(false);
  const [orderedIds, setOrderedIds] = useState([]); // ids de shipments en orden óptimo
  const [routeInfo, setRouteInfo] = useState(null); // { distance_km, duration_min }
  const [detail, setDetail] = useState(null); // shipment abierto en modal

  // Separa envíos con coord vs sin coord
  const { withCoords, withoutCoords } = useMemo(() => {
    const w = [];
    const wo = [];
    for (const s of shipments) {
      const lat = Number(s.shipping_lat);
      const lng = Number(s.shipping_lng);
      if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
        w.push(s);
      } else {
        wo.push(s);
      }
    }
    return { withCoords: w, withoutCoords: wo };
  }, [shipments]);

  const [branchFilter, setBranchFilter] = useState(() => {
    const saved = localStorage.getItem('deliverer_branch_tab');
    // Migración: si venía 'all' de una versión previa, forzar a 'morelos'
    if (!saved || saved === 'all') return 'morelos';
    return saved;
  });
  useEffect(() => { localStorage.setItem('deliverer_branch_tab', branchFilter); }, [branchFilter]);

  // Conteo de envíos pendientes por sucursal (para los badges de las tabs)
  const [branchCounts, setBranchCounts] = useState({ morelos: 0, america: 0 });

  // Cargar envíos pendientes: pide ambas sucursales en paralelo para poder
  // (a) mostrar los envíos de la sucursal actualmente seleccionada en el mapa
  // y (b) actualizar el contador de la otra sucursal (badge rojo en la tab).
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [morResp, ameResp] = await Promise.all([
          dax.get('/public/deliverer/pending-shipments?branch=morelos'),
          dax.get('/public/deliverer/pending-shipments?branch=america'),
        ]);
        const morShips = morResp.data.shipments || [];
        const ameShips = ameResp.data.shipments || [];
        setBranchCounts({ morelos: morShips.length, america: ameShips.length });
        setShipments(branchFilter === 'america' ? ameShips : morShips);
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudieron cargar los envíos');
      } finally {
        setLoading(false);
      }
    })();
  }, [branchFilter]);

  // Inicializar el mapa
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setMapError('Falta REACT_APP_MAPBOX_TOKEN');
      return;
    }
    if (!mapContainer.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [branch.lng, branch.lat],
      zoom: 12,
    });
    map.on('error', (e) => {
      console.error('[Mapbox error]', e);
      const err = e?.error;
      setMapError(err?.message || 'Error cargando el mapa');
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;

    // Marker sucursal
    const branchEl = document.createElement('div');
    branchEl.style.cssText = 'width:38px;height:38px;background:#dc2626;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:18px;';
    branchEl.textContent = '🏪';
    branchMarkerRef.current = new mapboxgl.Marker({ element: branchEl })
      .setLngLat([branch.lng, branch.lat])
      .setPopup(new mapboxgl.Popup({ offset: 22 }).setText(branch.name))
      .addTo(map);

    return () => { try { map.remove(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mover el pin de sucursal al cambiar branch
  useEffect(() => {
    if (!mapRef.current || !branchMarkerRef.current) return;
    branchMarkerRef.current.setLngLat([branch.lng, branch.lat]);
    branchMarkerRef.current.getPopup().setText(branch.name);
    // limpiar orden previo porque cambió origen
    setOrderedIds([]);
    setRouteInfo(null);
    if (mapRef.current.getLayer('route')) mapRef.current.removeLayer('route');
    if (mapRef.current.getSource('route')) mapRef.current.removeSource('route');
    // ajustar view
    fitToShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  // Al cambiar los envíos con coord, redibujar markers y fit bounds
  useEffect(() => {
    if (!mapRef.current) return;
    drawMarkers(withCoords, []);
    fitToShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withCoords]);

  // Cuando cambia orderedIds, redibujar markers numerados
  useEffect(() => {
    if (!mapRef.current) return;
    drawMarkers(withCoords, orderedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedIds]);

  const drawMarkers = (list, orderIds) => {
    const map = mapRef.current;
    if (!map) return;
    // Limpiar markers previos
    markersRef.current.forEach((m) => { try { m.remove(); } catch { /* noop */ } });
    markersRef.current = [];
    // Numeración: si hay orden óptimo, usarlo; si no, orden natural
    const orderMap = new Map();
    if (orderIds && orderIds.length) {
      orderIds.forEach((id, idx) => orderMap.set(id, idx + 1));
    } else {
      list.forEach((s, idx) => orderMap.set(s.id, idx + 1));
    }
    for (const s of list) {
      const num = orderMap.get(s.id) || '?';
      const isDelivered = !!s.delivered_at;
      const el = document.createElement('div');
      const bg = isDelivered ? '#059669' : '#2563eb';
      el.style.cssText = `width:34px;height:34px;background:${bg};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:14px;cursor:pointer`;
      el.textContent = isDelivered ? '✓' : String(num);
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([Number(s.shipping_lng), Number(s.shipping_lat)])
        .addTo(map);
      // Al clic en el marker abrimos el modal de detalle con productos
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        setDetail(s);
      });
      markersRef.current.push(marker);
    }
  };

  const fitToShipments = () => {
    const map = mapRef.current;
    if (!map) return;
    if (!withCoords.length) {
      map.flyTo({ center: [branch.lng, branch.lat], zoom: 12 });
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([branch.lng, branch.lat]);
    withCoords.forEach((s) => bounds.extend([Number(s.shipping_lng), Number(s.shipping_lat)]));
    map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
  };

  const optimizeRoute = async () => {
    if (!withCoords.length) return;
    setOptimizing(true);
    setError('');
    setRouteInfo(null);
    try {
      // Mapbox Optimization API v1: los pares válidos son
      //   roundtrip=true  → source=first, destination=any|last (regresa al origen)
      //   roundtrip=false → source=first, destination=last (NO acepta 'any' aquí)
      // Para "solo ida", como no queremos regresar y necesitamos un 'last' fijo,
      // ponemos como último punto al destino más lejano del origen (heurística
      // simple para que la ruta termine razonablemente lejos, en vez de forzar
      // al usuario a elegirlo a mano).
      const MAX_DEST = 11;
      let usable = withCoords.slice(0, MAX_DEST);
      const leftover = withCoords.slice(MAX_DEST);

      if (!roundtrip && usable.length > 1) {
        const dist2 = (s) => {
          const dx = Number(s.shipping_lng) - branch.lng;
          const dy = Number(s.shipping_lat) - branch.lat;
          return dx * dx + dy * dy;
        };
        let farIdx = 0;
        let farD = -1;
        usable.forEach((s, i) => { const d = dist2(s); if (d > farD) { farD = d; farIdx = i; } });
        if (farIdx !== usable.length - 1) {
          const arr = usable.slice();
          const [pick] = arr.splice(farIdx, 1);
          arr.push(pick);
          usable = arr;
        }
      }

      const coords = [[branch.lng, branch.lat], ...usable.map((s) => [Number(s.shipping_lng), Number(s.shipping_lat)])];
      const coordStr = coords.map(([lng, lat]) => `${lng},${lat}`).join(';');
      const params = new URLSearchParams({
        source: 'first',
        destination: 'last',
        roundtrip: roundtrip ? 'true' : 'false',
        geometries: 'geojson',
        overview: 'full',
        access_token: MAPBOX_TOKEN,
      });
      const url = `https://api.mapbox.com/optimized-trips/v1/mapbox/driving/${coordStr}?${params.toString()}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.code !== 'Ok' || !data.trips?.[0]) {
        throw new Error(data.message || 'No se pudo optimizar la ruta');
      }
      const trip = data.trips[0];
      const waypoints = data.waypoints || [];
      // waypoint_index 0 corresponde al origen (branch)
      // Ordenamos los destinos por su waypoint_index (excluyendo el origen).
      const ordered = usable
        .map((s, i) => ({ s, wpIndex: waypoints[i + 1]?.waypoint_index ?? (i + 1) }))
        .sort((a, b) => a.wpIndex - b.wpIndex)
        .map(({ s }) => s.id);
      setOrderedIds(ordered);

      // Pintar la ruta
      const map = mapRef.current;
      const geojson = { type: 'Feature', properties: {}, geometry: trip.geometry };
      if (map.getSource('route')) {
        map.getSource('route').setData(geojson);
      } else {
        map.addSource('route', { type: 'geojson', data: geojson });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#dc2626', 'line-width': 4, 'line-opacity': 0.85 },
        });
      }
      setRouteInfo({
        distance_km: (trip.distance || 0) / 1000,
        duration_min: (trip.duration || 0) / 60,
        skipped: leftover.length,
      });
    } catch (e) {
      console.error('[optimize] error', e);
      setError(e.message || 'Error optimizando la ruta');
    } finally {
      setOptimizing(false);
    }
  };

  const openInMaps = (s) => {
    const lat = Number(s.shipping_lat);
    const lng = Number(s.shipping_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    // Google Maps deep-link con navegación desde la posición actual
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const markDelivered = async (s) => {
    if (!window.confirm(`¿Marcar "${s.client_name}" como entregado?`)) return;
    try {
      await dax.post(`/public/deliverer/shipments/${s.id}/mark-delivered`);
      setShipments((prev) => prev.map((x) => x.id === s.id ? { ...x, delivered_at: new Date().toISOString() } : x));
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo marcar como entregado');
    }
  };

  const undoDelivered = async (s) => {
    try {
      await dax.post(`/public/deliverer/shipments/${s.id}/undo-delivered`);
      setShipments((prev) => prev.map((x) => x.id === s.id ? { ...x, delivered_at: null } : x));
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo deshacer');
    }
  };

  // Lista ordenada para el sidebar
  const listOrdered = useMemo(() => {
    if (orderedIds.length) {
      const byId = new Map(withCoords.map((s) => [s.id, s]));
      return orderedIds.map((id) => byId.get(id)).filter(Boolean);
    }
    return withCoords;
  }, [withCoords, orderedIds]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <style>{DEFENSIVE_CSS}</style>
      <header className="bg-red-600 text-white px-4 pb-3 shadow flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button
          onClick={onBack}
          className="text-white/90 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center -ml-2 rounded-full active:bg-red-700"
          data-testid="pending-back-btn"
          aria-label="Volver"
        >‹</button>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wide opacity-80">Portal Entregador</p>
          <h1 className="text-base sm:text-lg font-bold truncate">📍 Envíos pendientes ({shipments.length})</h1>
        </div>
      </header>

      {/* Controles */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs" data-testid="deliverer-branch-tabs">
          <span className="text-[10px] uppercase text-slate-500 pl-1">Sucursal:</span>
          {[
            { id: 'morelos', label: 'Morelos' },
            { id: 'america', label: 'América' },
          ].map(t => {
            const count = branchCounts[t.id] || 0;
            const isActive = branchFilter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setBranchFilter(t.id)}
                className={`relative px-2.5 py-1 rounded-md font-medium ${isActive ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                data-testid={`branch-tab-${t.id}`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow border border-white"
                    data-testid={`branch-tab-${t.id}-count`}
                    title={`${count} envío(s) pendiente(s) en ${t.label}`}
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Salgo desde:</label>
          <select
            value={branch.id}
            onChange={(e) => setBranch(BRANCHES.find((b) => b.id === e.target.value) || BRANCHES[0])}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            data-testid="pending-branch-select"
          >
            {BRANCHES.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
          </select>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs">
          <button
            onClick={() => setRoundtrip(true)}
            className={`px-3 py-1.5 rounded-md font-medium ${roundtrip ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
            data-testid="pending-loop-roundtrip"
          >🔁 Ida y vuelta</button>
          <button
            onClick={() => setRoundtrip(false)}
            className={`px-3 py-1.5 rounded-md font-medium ${!roundtrip ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
            data-testid="pending-loop-oneway"
          >➡️ Solo ida</button>
        </div>
        <div className="flex-1" />
        <button
          onClick={optimizeRoute}
          disabled={optimizing || !withCoords.length}
          className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2 rounded-lg btn-transition"
          data-testid="pending-optimize-btn"
        >
          {optimizing ? 'Calculando…' : '🧭 Calcular ruta óptima'}
        </button>
      </div>

      {/* Info de la ruta */}
      {routeInfo && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 text-xs text-emerald-800" data-testid="pending-route-info">
          Ruta lista: <b>{routeInfo.distance_km.toFixed(1)} km</b> · aprox <b>{Math.round(routeInfo.duration_min)} min</b>
          {routeInfo.skipped > 0 && (
            <span className="ml-2 text-amber-700">· ⚠️ {routeInfo.skipped} envío(s) no incluidos (máx 11 por ruta)</span>
          )}
        </div>
      )}
      {error && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 text-xs text-rose-800" data-testid="pending-error">{error}</div>
      )}
      {mapError && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800">Mapa: {mapError}</div>
      )}

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Mapa */}
        <div className="relative h-[45vh] lg:h-auto lg:flex-1 min-h-[300px]">
          <div ref={mapContainer} className="absolute inset-0" data-testid="pending-map" />
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-slate-500">
              Cargando envíos…
            </div>
          )}
        </div>

        {/* Lista lateral */}
        <div className="lg:w-[380px] bg-white lg:border-l border-slate-200 overflow-y-auto max-h-[55vh] lg:max-h-none">
          {!loading && withCoords.length === 0 && withoutCoords.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="pending-empty">
              🎉 No tienes envíos pendientes ahora mismo.
            </div>
          )}

          {withCoords.length > 0 && (
            <div className="p-3 space-y-2" data-testid="pending-list">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold px-1">
                {orderedIds.length ? 'Orden óptimo' : 'Envíos a entregar'}
              </p>
              {listOrdered.map((s, idx) => (
                <div key={s.id} className="border border-slate-200 rounded-lg p-3 hover:border-red-300 transition cursor-pointer" data-testid={`pending-item-${s.id}`} onClick={() => setDetail(s)}>
                  <div className="flex items-start gap-2">
                    <div className="w-7 h-7 flex-shrink-0 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 break-words">{s.client_name || 'Cliente'}</p>
                      {s.folio && <p className="text-[11px] text-slate-400">Folio: {s.folio}</p>}
                      {(s.shipping_address || s.shipping_colonia) && (
                        <p className="text-xs text-slate-600 mt-1 break-words">{s.shipping_address || s.shipping_colonia}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] mt-1.5">
                        {(() => {
                          const t = Number(s.total) > 0 ? Number(s.total) : Number(s.items_total || 0) + Number(s.shipping_cost || 0);
                          return t > 0 ? <span className="text-emerald-700">💰 ${t.toFixed(2)}</span> : null;
                        })()}
                        {typeof s.shipping_cost === 'number' && <span className="text-blue-700">🚚 ${Number(s.shipping_cost).toFixed(2)}</span>}
                        {s.client_phone && <span className="text-slate-500">📞 {s.client_phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openInMaps(s)}
                      className="flex-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-2 py-1.5 font-medium"
                      data-testid={`pending-open-maps-${s.id}`}
                    >🗺️ Maps</button>
                    {s.delivered_at ? (
                      <button
                        onClick={() => undoDelivered(s)}
                        className="flex-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-md px-2 py-1.5 font-medium"
                        data-testid={`pending-undo-delivered-${s.id}`}
                      >✅ Entregado (deshacer)</button>
                    ) : (
                      <button
                        onClick={() => markDelivered(s)}
                        className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-2 py-1.5 font-medium"
                        data-testid={`pending-mark-delivered-${s.id}`}
                      >✓ Entregado</button>
                    )}
                    {s.client_phone && (
                      <a
                        href={`tel:${s.client_phone}`}
                        className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 font-medium"
                      >📞</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {withoutCoords.length > 0 && (
            <div className="p-3 space-y-2 border-t border-slate-200" data-testid="pending-without-coords">
              <p className="text-[11px] uppercase tracking-wide text-amber-600 font-semibold px-1">
                ⚠️ Sin ubicación en mapa ({withoutCoords.length})
              </p>
              {withoutCoords.map((s) => (
                <div key={s.id} className="border border-amber-200 bg-amber-50 rounded-lg p-2.5 cursor-pointer" onClick={() => setDetail(s)}>
                  <p className="font-semibold text-slate-800 text-sm break-words">{s.client_name || 'Cliente'}</p>
                  {(s.shipping_address || s.shipping_colonia) && (
                    <p className="text-xs text-slate-600 mt-0.5 break-words">{s.shipping_address || s.shipping_colonia}</p>
                  )}
                  {s.client_phone && <p className="text-xs text-slate-500">📞 {s.client_phone}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalle: se abre al clic en pin o en tarjeta */}
      <ShipmentDetailModal
        shipment={detail}
        onClose={() => setDetail(null)}
        actions={detail && (
          <>
            <button
              onClick={() => { openInMaps(detail); }}
              className="flex-1 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-2 font-medium"
            >🗺️ Abrir en Maps</button>
            {detail.delivered_at ? (
              <button
                onClick={async () => { await undoDelivered(detail); setDetail({ ...detail, delivered_at: null }); }}
                className="flex-1 text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-lg px-3 py-2 font-medium"
              >✅ Deshacer entrega</button>
            ) : (
              <button
                onClick={async () => { await markDelivered(detail); setDetail({ ...detail, delivered_at: new Date().toISOString() }); }}
                className="flex-1 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-2 font-medium"
              >✓ Marcar entregado</button>
            )}
          </>
        )}
      />
    </div>
  );
}
